<script setup lang="ts">
import { computed, markRaw, ref, shallowRef, watch } from "vue";
import { getLegacyApp, OptionsData } from "../../core/legacy-bridge";
import { t } from "../../i18n";
import { appStore } from "../../stores/app";
import { CustomOption } from "../../types/type_def";
import AppModal from "../common/AppModal.vue";

// Live configuration/module objects from app.ts: not made reactive, changed in place like before
const data = shallowRef<OptionsData | null>(null);

watch(() => appStore.modal, async modal => {
    if (modal !== "options") return;
    data.value = null;
    data.value = markRaw(await getLegacyApp().loadOptions());
});

const legPackages = computed(() => {
    const packages = data.value?.series.legPackages ?? [];
    return packages.length > 1 ? packages : [];
});

/** Options priced as "Frame Cushion Item"; per-module options list the modules they apply to */
const customOptions = computed(() => {
    if (!data.value) return [];
    const { series, summaryElements, configuration } = data.value;
    return series.customOptions
        .filter(option => option.priceLists.some(p => p.productType == "Frame Cushion Item"))
        .map(option => ({
            option,
            checked: configuration.data.customOptions?.find(e => e.id == option.id)?.value == option.id,
            modules: option.perModule
                ? summaryElements
                    .filter(element => option.priceLists.some(p => p.items.includes(element.variantName)))
                    .map(element => ({
                        entityId: element.entity,
                        name: element.variantName,
                        checked: element.extras?.find(e => e.optionId == option.id)?.optionValue == option.id,
                    }))
                : null,
        }));
});

function ensureCustomOptions() {
    const configuration = data.value.configuration;
    if (configuration.data.customOptions == undefined) {
        configuration.data.customOptions = [];
    }
}

async function onLegChanged(value: string) {
    ensureCustomOptions();
    data.value.configuration.data.leg = value;
    await getLegacyApp().onOptionsChanged();
}

/** Stores { id, value } (both the option id) on the configuration, or on one module's entity */
async function onOptionToggled(option: CustomOption, selected: boolean, entityId?: string) {
    ensureCustomOptions();
    const owner = entityId
        ? data.value.module.entities.find(e => e.id == entityId)
        : data.value.configuration.data;
    owner.customOptions = (owner.customOptions ?? []).filter(e => e.id != option.id);
    if (selected) {
        owner.customOptions.push({ id: option.id, value: option.id });
    }
    await getLegacyApp().onOptionsChanged();
}

const isRendering = ref(false);
async function renderImages() {
    isRendering.value = true;
    try {
        await getLegacyApp().renderProductImages();
    } finally {
        isRendering.value = false;
    }
}
</script>

<template>
    <AppModal class="options-modal" name="options" :title="t('modal.options.header')">
        <div class="options">
            <div v-if="legPackages.length" class="field mt-5">
                <label class="label">Legs</label>
                <div class="control">
                    <div class="select">
                        <select data-option="leg" @change="onLegChanged(($event.target as HTMLSelectElement).value)">
                            <option v-for="legPackage in legPackages" :key="legPackage.identifier" :value="legPackage.identifier"
                                :selected="data.configuration.data.leg == legPackage.identifier"
                                v-html="legPackage.name || legPackage.identifier"></option>
                        </select>
                    </div>
                </div>
            </div>

            <div v-for="{ option, checked, modules } in customOptions" :key="option.id" class="field mt-5">
                <label class="label" v-html="option.name"></label>
                <div class="control">
                    <template v-if="modules">
                        <p v-for="module in modules" :key="module.entityId" class="mt-5">
                            <label>{{ module.name }}</label><br>
                            <label class="checkbox">
                                <input type="checkbox" :checked="module.checked" @change="onOptionToggled(option, ($event.target as HTMLInputElement).checked, module.entityId)">&nbsp;{{ option.name }}
                            </label>
                        </p>
                    </template>
                    <label v-else class="checkbox">
                        <input type="checkbox" :checked="checked" @change="onOptionToggled(option, ($event.target as HTMLInputElement).checked)">&nbsp;{{ option.name }}
                    </label>
                </div>
            </div>
        </div>
        <p class="mt-5">
            <button class="renderConfiguration button is-radiusless" :disabled="isRendering" @click="renderImages">Download 4 product images</button>
        </p>
    </AppModal>
</template>
